/**
 * Custom React Hook for Resident Management
 * Handles state, pagination, search, and API interactions
 */

import { useState, useEffect, useCallback } from 'react';
import residentService from '../services/residentService';
import {
  ResidentDTO,
  CreateResidentRequest,
  PageResponse,
} from '../types/resident.types';

interface UseResidentsReturn {
  // State
  residents: ResidentDTO[];
  selectedResident: ResidentDTO | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  totalElements: number;
  totalPages: number;
  currentPage: number;

  // Actions
  fetchResidents: (page?: number, size?: number) => Promise<void>;
  searchResidents: (query: string, page?: number, size?: number) => Promise<void>;
  getResidentById: (id: string) => Promise<void>;
  createResident: (data: CreateResidentRequest) => Promise<ResidentDTO>;
  updateResident: (id: string, data: Partial<ResidentDTO>) => Promise<ResidentDTO>;
  dischargeResident: (id: string, reason: string) => Promise<void>;
  deleteResident: (id: string) => Promise<void>;
  setSelectedResident: (resident: ResidentDTO | null) => void;
  clearError: () => void;

  // Pagination
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

/**
 * Custom hook for managing residents
 */
export const useResidents = (): UseResidentsReturn => {
  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  // Fetch all residents (paginated)
  const fetchResidents = useCallback(
    async (page: number = currentPage, size: number = pageSize) => {
      try {
        setLoading(true);
        setError(null);

        const response: PageResponse<ResidentDTO> = await residentService.getAllResidents(
          page,
          size
        );

        setResidents(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        setCurrentPage(response.number);
      } catch (err: any) {
        console.error('Error fetching residents:', err);
        setError(err.response?.data?.message || 'Failed to load residents');
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  // Search residents by name
  const searchResidents = useCallback(
    async (query: string, page: number = 0, size: number = pageSize) => {
      if (!query.trim()) {
        fetchResidents(page, size);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response: PageResponse<ResidentDTO> = await residentService.searchResidents(
          query,
          page,
          size
        );

        setResidents(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages);
        setCurrentPage(response.number);
      } catch (err: any) {
        console.error('Error searching residents:', err);
        setError(err.response?.data?.message || 'Failed to search residents');
      } finally {
        setLoading(false);
      }
    },
    [pageSize, fetchResidents]
  );

  // Get resident by ID
  const getResidentById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const resident = await residentService.getResidentById(id);
      setSelectedResident(resident);
    } catch (err: any) {
      console.error('Error fetching resident:', err);
      setError(err.response?.data?.message || 'Failed to load resident');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new resident
  const createResident = useCallback(
    async (data: CreateResidentRequest): Promise<ResidentDTO> => {
      try {
        setSaving(true);
        setError(null);

        const newResident = await residentService.createResident(data);

        // Refresh list
        await fetchResidents(currentPage, pageSize);

        return newResident;
      } catch (err: any) {
        console.error('Error creating resident:', err);
        setError(err.response?.data?.message || 'Failed to create resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [fetchResidents, currentPage, pageSize]
  );

  // Update resident
  const updateResident = useCallback(
    async (id: string, data: Partial<ResidentDTO>): Promise<ResidentDTO> => {
      try {
        setSaving(true);
        setError(null);

        const updated = await residentService.updateResident(id, data);

        // Update in list
        setResidents((prev) =>
          prev.map((r) => (r.id === id ? updated : r))
        );

        // Update selected if it's the same resident
        if (selectedResident?.id === id) {
          setSelectedResident(updated);
        }

        return updated;
      } catch (err: any) {
        console.error('Error updating resident:', err);
        setError(err.response?.data?.message || 'Failed to update resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident]
  );

  // Discharge resident
  const dischargeResident = useCallback(
    async (id: string, reason: string) => {
      try {
        setSaving(true);
        setError(null);

        const discharged = await residentService.dischargeResident(id, reason);

        // Update in list
        setResidents((prev) =>
          prev.map((r) => (r.id === id ? discharged : r))
        );

        // Update selected if it's the same resident
        if (selectedResident?.id === id) {
          setSelectedResident(discharged);
        }
      } catch (err: any) {
        console.error('Error discharging resident:', err);
        setError(err.response?.data?.message || 'Failed to discharge resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident]
  );


  // Delete resident
  const deleteResident = useCallback(
    async (id: string) => {
      try {
        setSaving(true);
        setError(null);

        await residentService.deleteResident(id);

        // Remove from list
        setResidents((prev) => prev.filter((r) => r.id !== id));

        // Clear selected if it's the same resident
        if (selectedResident?.id === id) {
          setSelectedResident(null);
        }

        // Refresh list to get updated pagination
        await fetchResidents(currentPage, pageSize);
      } catch (err: any) {
        console.error('Error deleting resident:', err);
        setError(err.response?.data?.message || 'Failed to delete resident');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [selectedResident, fetchResidents, currentPage, pageSize]
  );

  // Pagination handlers
  const setPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchResidents(page, pageSize);
    },
    [fetchResidents, pageSize]
  );

  const handleSetPageSize = useCallback(
    (size: number) => {
      setPageSize(size);
      setCurrentPage(0);
      fetchResidents(0, size);
    },
    [fetchResidents]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    fetchResidents();
  }, []); // Empty dependency - only fetch once on mount

  return {
    // State
    residents,
    selectedResident,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,

    // Actions
    fetchResidents,
    searchResidents,
    getResidentById,
    createResident,
    updateResident,
    dischargeResident,
    deleteResident,
    setSelectedResident,
    clearError,

    // Pagination
    setPage,
    setPageSize: handleSetPageSize,
  };
};
